import moment from "moment";
import { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, Dropdown } from "react-bootstrap";
import { CloseIcon, MoreIcon, EditIcon, Check2Icon } from "@components/icons";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";

import Spinner from "@components/Spinner/Spinner";
import ServerError from "@components/Error/ServerError";
import PageNotFound from "@components/Error/PageNotFound";
import ExportSensor from "@modules/ExportSensor/ExportSensor";
import {
  getSensor,
  sensorPlot,
  installationPlot,
  getLastSend,
  refreshSensorHealth,
  patchSensor,
} from "../services/sensorCrud";
import SensorChart from "@modules/Trees/components/SensorGraph/SensorChart";
import ReactTable from "@components/ReactTable";
import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";
import "./SensorDetail.css";

const style = {
  height: "260px",
  width: "100%",
};
const sensorState = {
  0: "working",
  1: "battery_empty",
  2: "maintenace_needed",
  3: "not_sending",
  4: "unknown",
  5: "defect",
};

const SensorDetailRow = ({ label, children }) => (
  <div className="entity-detail-data-row">
    <div className="entity-detail-data-label">{label}</div>
    <div className="entity-detail-data-value">{children}</div>
  </div>
);

export function SensorPlot({ sensor, plotType, setPlotType, installation }) {
  const [plot, setPlot] = useState();
  const [plotTitle, setPlotTitle] = useState("");
  const { t } = useTranslation();
  const scheduledPlotQuery = createScheduledQueryFn(
    () => (installation ? installationPlot(installation) : sensorPlot(sensor)),
    REQUEST_PRIORITIES.NORMAL,
    "sensorPlot"
  );
  const { isLoading: isPlotLoading } = useQuery(
    ["sensorplot" + sensor.id, { sensor }],
    scheduledPlotQuery,
    {
      enabled: !!sensor,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const { x, y } = data;
        let sensorType = "resistance";
        let plotTitle = "Resistance in kΩ";
        sensor.soil_moisture_sensor
          ? ((sensorType = "soilMoisture"), (plotTitle = "Soil Moisture in %"))
          : sensor.soil_water_tension_sensor
          ? ((sensorType = "soilWaterTension"), (plotTitle = "Soil Water Tension in kPa"))
          : sensor.dendrometer_sensor
          ? ((sensorType = "dendrometer"), (plotTitle = "Diameter in µm"))
          : (sensorType = "resistance");
        setPlotType(sensorType);
        setPlotTitle(plotTitle);

        let d;
        if (sensorType == "resistance") {
          // d = x[sensor.hardware_serial]?.resistance?.map((value, index) => {
          //   if (y[sensor.hardware_serial].resistance[index]) {
          //     return [value, y[sensor.hardware_serial].resistance[index]];
          //   }
          // });
          const mainData =
            x[sensor.id]?.resistance?.map((value, index) => [
              value,
              y[sensor.id].resistance[index],
            ]) || [];
          // const resis70mcData =
          //   x[sensor.hardware_serial]?.resistance?.map((value, index) => [
          //     value,
          //     y[sensor.hardware_serial].resis_70mc[index],
          //   ]) || [];
          // const resis50mcData =
          //   x[sensor.hardware_serial]?.resistance?.map((value, index) => [
          //     value,
          //     y[sensor.hardware_serial].resis_50mc[index],
          //   ]) || [];

          d = [
            { data: mainData, name: "resistance", type: "line", showSymbol: false }, // Assuming blue for the main resistance line
            // {
            //   data: resis70mcData,
            //   color: GREEN,
            //   name: "resistance for 70%",
            //   type: "line",
            //   showSymbol: false,
            // },
            // {
            //   data: resis50mcData,
            //   color: RED,
            //   name: "resistance for 50%",
            //   type: "line",
            //   showSymbol: false,
            // },
          ];
        }
        if (sensorType == "soilMoisture") {
          const mainData =
            x[sensor.id]?.water_soil?.map((value, index) => [
              value,
              y[sensor.id].water_soil[index],
            ]) || [];
          d = [
            {
              data: mainData,
              color: "blue",
              name: "soil moisture",
              type: "line",
              showSymbol: false,
            }, // Assuming blue for the main resistance line
          ];
        }
        if (sensorType == "soilWaterTension") {
          const mainData =
            x[sensor.id]?.soil_water_tension?.map((value, index) => [
              value,
              y[sensor.id].soil_water_tension[index],
            ]) || [];
          d = [
            {
              data: mainData,
              color: "blue",
              name: "soil water tension",
              type: "line",
              showSymbol: false,
            }, // Assuming blue for the main resistance line
          ];
        }
        if (sensorType == "dendrometer") {
          const mainData =
            x[sensor.id]?.dendrometer?.map((value, index) => [
              value,
              y[sensor.id].dendrometer[index],
            ]) || [];
          d = [
            {
              data: mainData,
              color: "blue",
              name: "diameter",
              type: "line",
              showSymbol: false,
            }, // Assuming blue for the main resistance line
          ];
        }
        const plotNew = d ? [...d] : null;
        setPlot(plotNew);
      },
    }
  );
  return (
    <>
      {isPlotLoading ? (
        <Spin />
      ) : plot ? (
        <SensorChart plot={plot} style={style} type={plotType} plotTitle={plotTitle} />
      ) : (
        <div className=" bg-light text-center" style={{ height: "15vh" }}>
          <img name="empty_project" alt="" width="80" src="/empty_project.svg" />
          <h6 className="my-4">{t("sensor_details.no_data_found")}</h6>
        </div>
      )}
    </>
  );
}

export default function SensorDetail() {
  const [plotType, setPlotType] = useState("resistance");
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const id = query.get("selectedSensor");
  const [lastSend, setLastSend] = useState("");
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  // Inline editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  // Mutation for updating sensor (using PATCH to only update specified fields)
  const updateSensorMutation = useMutation(
    (data) => patchSensor({ id: data.sensorId, data: data.formData }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sensors", { id }]);
        queryClient.invalidateQueries(["sensors"]);
        setIsEditingName(false);
      },
    }
  );

  const handleStartEditName = useCallback((currentName) => {
    setEditedName(currentName || "");
    setIsEditingName(true);
  }, []);

  const handleSaveName = useCallback(
    (sensor) => {
      if (!editedName.trim()) return;
      updateSensorMutation.mutate({
        sensorId: sensor.id,
        formData: { name: editedName.trim() },
      });
    },
    [editedName, updateSensorMutation]
  );

  const handleCancelEditName = useCallback(() => {
    setIsEditingName(false);
    setEditedName("");
  }, []);

  //get sensor detail
  const scheduledGetSensor = createScheduledQueryFn(
    () => getSensor(id),
    REQUEST_PRIORITIES.HIGH,
    "getSensor"
  );
  const {
    data: sensor,
    isLoading,
    isError,
    error,
  } = useQuery(["sensors", { id }], scheduledGetSensor, {
    refetchOnWindowFocus: false,
    retry: 3, // number of times to retry the request before giving up
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // delay between retries in milliseconds
    // retry: false,
    enabled: !!id,
  });

  const scheduledGetLastSend = createScheduledQueryFn(
    () => getLastSend(id),
    REQUEST_PRIORITIES.NORMAL,
    "getLastSend"
  );
  const { isLoading: isLoadingLastSend, data: lastSendData } = useQuery(
    ["last-sent", { id }],
    scheduledGetLastSend,
    {
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      enabled: !!id,
    }
  );
  const data = lastSendData?.data ?? null;
  const hasLastSendData = Array.isArray(data) && data.length > 0;
  const sensorType = lastSendData?.sensor ?? "";
  const value = lastSendData?.value ?? "";

  const handleRefreshHealth = async () => {
    if (!sensor?.id || isRefreshing) return;

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      const response = await refreshSensorHealth(sensor.id);

      // Immediately update and refetch all sensor-related queries
      if (response?.sensor) {
        // Update the cache for both number and string IDs
        queryClient.setQueryData(["sensors", { id: sensor.id }], response.sensor);
        queryClient.setQueryData(["sensors", { id: String(sensor.id) }], response.sensor);

        // Force refetch of all sensor-related queries
        await Promise.all([
          queryClient.refetchQueries(["sensors"]),
          queryClient.refetchQueries(["sensors-paginated"]),
        ]);
      }
    } catch (error) {
      console.error("Failed to refresh sensor health:", error);
      setRefreshError(t("refresh_health_error") || "Failed to refresh health data");
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatLastUpdated = (lastUpdated) => {
    if (!lastUpdated) return null;
    try {
      const locale = i18n.language === "de" ? de : enUS;
      const distance = formatDistanceToNow(new Date(lastUpdated), {
        addSuffix: true,
        locale,
      });

      if (i18n.language === "de") {
        return `Aktualisiert ${distance}`;
      }

      return `Updated ${distance}`;
    } catch (error) {
      return null;
    }
  };

  if (!id) return null;
  if (isLoading) return <Spinner height={"80vh"} />;
  if (isError && error.response.status === 404) {
    return <PageNotFound />;
  }
  if (isError) {
    return <ServerError />;
  }
  if (!sensor?.id) return null;

  const handleClose = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selectedSensor");
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`, {
      replace: true,
    });
  };

  const now = new Date();
  const hours_passed = (now - new Date(lastSend)) / (1000 * 60 * 60);
  let isSending = false;
  if (hours_passed < 3) {
    isSending = true;
  }
  let isSendingObj = {};
  try {
    const stored = localStorage.getItem("isSending");
    isSendingObj = stored ? JSON.parse(stored) : {};
  } catch (e) {
    isSendingObj = {};
  }

  isSendingObj[sensor.id] = isSending;

  localStorage.setItem("isSending", JSON.stringify(isSendingObj));

  return (
    <>
      <Card className="h-100 sensor-detail-panel entity-detail-panel">
        <Card.Header className="sensor-detail-panel-header entity-detail-header">
          <div className="d-flex align-items-center justify-content-between gap-2 w-100 entity-detail-header-row">
            <button
              type="button"
              className="sensor-detail-close-btn entity-detail-control"
              onClick={handleClose}
              aria-label={t("close")}
              title={t("close")}
            >
              <CloseIcon />
            </button>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              {isEditingName ? (
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveName(sensor);
                      } else if (e.key === "Escape") {
                        handleCancelEditName();
                      }
                    }}
                    autoFocus
                    className="form-control form-control-sm"
                    style={{ maxWidth: "200px" }}
                    disabled={updateSensorMutation.isLoading}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0"
                    onClick={() => handleSaveName(sensor)}
                    disabled={updateSensorMutation.isLoading || !editedName.trim()}
                    title={t("save")}
                  >
                    {updateSensorMutation.isLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Check2Icon size="20px" style={{ color: "var(--bs-success)" }} />
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0"
                    onClick={handleCancelEditName}
                    disabled={updateSensorMutation.isLoading}
                    title={t("cancel")}
                  >
                    <CloseIcon size="20px" style={{ color: "var(--bs-danger)" }} />
                  </button>
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <h5
                    className="mb-0 entity-detail-title text-truncate"
                    data-testid="test-sensor-name"
                  >
                    {sensor.name}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0"
                    onClick={() => handleStartEditName(sensor.name)}
                    title={t("edit")}
                    style={{ opacity: 0.6 }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.6)}
                  >
                    <EditIcon size="18px" />
                  </button>
                </div>
              )}
            </div>
            <div className="sensor-detail-panel-actions d-flex align-items-center gap-2">
              <Dropdown align="end">
                <Dropdown.Toggle
                  as="button"
                  className="sensor-detail-icon-link sensor-detail-actions-toggle entity-detail-control entity-detail-actions-toggle border-0"
                  id="sensor-detail-actions-dropdown"
                  title={t("more_actions", "Mehr Aktionen")}
                >
                  <MoreIcon size="24px" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item as="div" className="px-2">
                    <ExportSensor
                      serials={[sensor.id]}
                      name={sensor.name}
                      sensors={[sensor]}
                      buttonClassName="btn btn-sm btn-outline-primary w-100"
                    />
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="sensor-detail-panel-body entity-detail-body">
          <div className="notes mb-4">
            <div className="entity-detail-data">
              <SensorDetailRow
                label={sensor.network_type == "LW" ? t("hardware_serial") : t("imei")}
              >
                {sensor.network_type == "LW" ? sensor.hardware_serial : sensor.imei}
              </SensorDetailRow>
              {sensor.pulse_sensor !== null && (
                <SensorDetailRow label={t("threshold")}>
                  <i>{sensor?.threshold}</i>
                </SensorDetailRow>
              )}
              <SensorDetailRow label={t("last_battery")}>
                {sensor.battery ? sensor.battery + "%" : t("unknown")}
              </SensorDetailRow>
              <SensorDetailRow label={t("sensor_state")}>
                <div className="d-flex flex-column w-100">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <span>
                      {
                        {
                          0: t(sensorState[0]),
                          1: t(sensorState[1]),
                          2: t(sensorState[2]),
                          3: isSending ? t(sensorState[0]) : t(sensorState[3]),
                          4: t(sensorState[4]),
                          5: t(sensorState[5]),
                        }[sensor.sensor_state]
                      }
                    </span>
                    <Tooltip title={t("refresh_health") || "Refresh health data"}>
                      <IconButton
                        onClick={handleRefreshHealth}
                        disabled={isRefreshing}
                        size="small"
                        color="primary"
                      >
                        {isRefreshing ? (
                          <CircularProgress size={20} />
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </div>
                  {sensor?.last_updated && (
                    <small style={{ color: "#666", fontSize: "0.85em" }}>
                      {formatLastUpdated(sensor.last_updated)}
                    </small>
                  )}
                  {refreshError && (
                    <small style={{ color: "#d32f2f", fontSize: "0.85em" }}>{refreshError}</small>
                  )}
                </div>
              </SensorDetailRow>
            </div>
            <div className="entity-detail-section-header mt-4">
              <h4 className="entity-detail-section-title" data-testid="test-last-10-measurements">
                {t("last_10_measurements")}
              </h4>
            </div>
            {isLoadingLastSend ? (
              <Spin />
            ) : hasLastSendData ? (
              <DataList
                data={data}
                // lastSend={lastSend}
                // lastSendValue={lastSendValue}
                sensorType={sensorType}
                value={value}
              />
            ) : (
              <div className="text-center py-4">
                <p className="mb-1 fw-semibold">{t("sensor_details.no_recent_measurements")}</p>
                <small className="text-muted">
                  {t("sensor_details.no_recent_measurements_hint")}
                </small>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
const dataList = [
  { time: "10:00", resistance: "5Ω", temperature: "22°C", battery: "90%" },
  { time: "10:05", resistance: "5.1Ω", temperature: "22.5°C", battery: "88%" },
  { time: "10:10", resistance: "5.2Ω", temperature: "23°C", battery: "85%" },
];

const DataListOld = () => {
  return (
    <div>
      <h2>Messwerte</h2>
      <ul>
        <li>
          <strong>Zeit</strong> - <strong>Widerstand</strong> - <strong>Temperatur</strong> -{" "}
          <strong>Batterie</strong>
        </li>
        {dataList.map((item, index) => (
          <li key={index}>
            {item.time} - {item.resistance} - {item.temperature} - {item.battery}
          </li>
        ))}
      </ul>
    </div>
  );
};

// <li className="pb-2">
//   <span style={{ fontWeight: "500" }}>{t("last_send")}</span>:{" "}
//   {moment(new Date(lastSend)).format("DD.MM.YYYY HH:mm")}
// </li>
// <li className="pb-2">
//   <span style={{ fontWeight: "500" }}>{t("last_value")}</span>: {lastSendValue}
// </li>
function DataTable({ lastSend, lastSendValue }) {
  // TODO: Depending on the sensor type, we need to define the headers of the table we want to show and then get them from the backend
  // Pulse: Widerstand, Temperatur, Batterie
  // FDR: Bodenfeuchte, Temperatur, Batterie
  // Watermark: SWT, Temperatur, Batterie
  const index = 0;
  const { t } = useTranslation();
  return (
    // <div style={{ maxWidth: "400px", margin: "20px auto", fontFamily: "Arial, sans-serif" }}>
    <div>
      <h2 style={{ fontWeight: "bold", textAlign: "center" }}>{t("last_measurements")}</h2>
      <table border="0" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ backgroundColor: "#ddd", borderBottom: "2px solid black" }}>
            <th style={{ textAlign: "left", padding: "10px" }}>{t("time")}</th>
            <th style={{ textAlign: "right", padding: "10px" }}>{t("resistance")}</th>
            {/* <th>Temperatur</th>
            <th>Batterie</th> */}
          </tr>
        </thead>
        <tbody>
          {/* {dataList.map((item, index) => ( */}
          {/* <tr key={index}> */}
          {/* <tr key={0}> */}
          <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff" }}>
            <td style={{ textAlign: "left", padding: "10px" }}>
              {moment(new Date(lastSend)).format("DD.MM.YYYY HH:mm")}
            </td>
            <td style={{ textAlign: "right", padding: "10px" }}>{lastSendValue}</td>
            {/* <td>{item.temperature}</td>
              <td>{item.battery}</td> */}
          </tr>
          {/* ))} */}
        </tbody>
      </table>
    </div>
  );
}

const Spin = () => {
  return (
    <div className="spin">
      <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
    </div>
  );
};

function DataList({ lastSend, lastSendValue, data, sensorType, value }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const timeString =
    lang == "de" ? "de-DE" : lang == "en" ? "en-UK" : lang == "it" ? "it-IT" : "de-DE";
  const tempHeader = t("temperature");
  const timeHeader = t("time");
  const valueHeader = value ? `${t(value)}` : t("resistance");
  const batHeader = t("battery");
  const columns = useMemo(() => {
    const cols = [
      {
        Header: timeHeader,
        accessor: "_time",
        Cell: ({ value }) => {
          const formattedDate = new Date(value).toLocaleString(timeString, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          return formattedDate;
        },
      },
      {
        Header: valueHeader,
        accessor: value || "resistance",
        Cell: ({ value }) => {
          if (value === undefined || value === null) return null;
          const roundedValue = value.toFixed(1);
          const unit =
            sensorType === "pulse"
              ? "kΩ"
              : sensorType === "soil_moisture"
              ? "%"
              : sensorType === "soil_water_tension"
              ? "kPa"
              : sensorType === "dendrometer"
              ? "µm"
              : "";
          return `${roundedValue} ${unit}`;
        },
      },
    ];

    if (sensorType !== "dendrometer") {
      cols.push({
        Header: tempHeader,
        accessor: "temperature",
        Cell: ({ value }) => {
          if (value === undefined || value === null) return null;
          const roundedTemperature = value.toFixed(1);
          return `${roundedTemperature} °C`;
        },
      });
    }

    cols.push({
      Header: batHeader,
      accessor: "battery",
      Cell: ({ value }) => {
        if (value === undefined || value === null) return null;
        const roundedValue = value.toFixed(3);
        return `${roundedValue} V`;
      },
    });

    return cols;
  }, [batHeader, sensorType, tempHeader, timeHeader, timeString, value, valueHeader]);
  return (
    <>
      {/* {" "} */}
      {/* {data && ( */}
      <ReactTable
        columns={columns}
        // data={[{ time: lastSend, value: lastSendValue }]}
        data={Array.isArray(data) ? data : []}
        // onRowClicked={onRowClicked}
        activeId={0}
        pagesize={10}
        size={[10, 20, 30, 40, 50]}
        pagination={false}
        filter={false}
      />
      {/* )} */}
    </>
  );
}
